import { plural } from '@cian/utils';
import type { IBuildingCard, IBuildingParking } from 'shared/types/buildingCard';
import { formatNumber, noBreak } from 'shared/utils/format';
import { NBSP } from 'shared/constants/text';

type TPriceKey = 'priceHourly' | 'priceMonthly' | 'priceEntry';

/**
 * Минимальная цена в рублях, округлённая вверх, как `parkings_agg` монолита.
 * Цены без валюты монолит отбрасывает (`convert_price_to_rur`), прочие валюты ждём в рублях от commercial-buildings
 */
function getMinRurPrice(parkings: IBuildingParking[], key: TPriceKey): number | null {
  const prices = parkings
    .filter(parking => parking.currency === 'rur' && parking[key])
    .map(parking => Math.ceil(parking[key] as number));

  return prices.length ? Math.min(...prices) : null;
}

/**
 * Значение «Парковка» в основном блоке. `null` — строку не выводим (парковок нет).
 * «, бесплатная» не выводится: в монолите агрегат не заполняет isFree, на проде подписи нет
 */
export function getParkingValue(card: IBuildingCard): string | null {
  const { parkings } = card;

  if (!parkings.length) {
    return null;
  }

  const placesCount = parkings.reduce((sum, parking) => sum + (parking.placesCount ?? 0), 0);
  const places = placesCount ? `${placesCount}${NBSP}${plural(placesCount, ['место', 'места', 'мест'])}` : '';

  const priceHourly = getMinRurPrice(parkings, 'priceHourly');
  const priceMonthly = getMinRurPrice(parkings, 'priceMonthly');
  const priceEntry = getMinRurPrice(parkings, 'priceEntry');

  let price = '';

  if (priceHourly) {
    price = ` от ${noBreak(formatNumber(priceHourly))}${NBSP}₽/ч`;
  } else if (priceMonthly) {
    price = ` от ${noBreak(formatNumber(priceMonthly))}${NBSP}₽/мес.`;
  } else if (priceEntry) {
    price = ` от ${noBreak(formatNumber(priceEntry))}${NBSP}за${NBSP}въезд`;
  }

  return `${places}${price}`;
}
